// Manual work-order creation for V2 (sidebar: "Create Work Order V2").
//
// Three shapes of work order, all from this one form:
//  - STOCK build: no customer order at all — just pick a product. The WO
//    carries no order link ("building for stock").
//  - Linked: optionally attach a customer order + line.
//  - "Empty" / top-level-only: makes ONLY the top-level BOM item — no
//    explosion, no sub-assembly work orders, no up-front reservations
//    (materials are checked and issued when the step starts). The
//    alternative mode runs the full BOM explosion exactly like sending an
//    order to V2.

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControl,
  FormControlLabel, FormLabel, List, ListItemButton, ListItemText,
  Paper, Radio, RadioGroup, TextField, Typography,
} from '@mui/material';
import { AddCircle, Clear, Factory, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { manufacturingAPI, v2 } from '../../../services/api';
import usePageTitle from '../../../hooks/usePageTitle';

const MANUFACTURABLE_TYPES = new Set(['manufactured', 'assembly', 'finished_good']);

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// Search-and-pick: a text field + result list. Deliberately simple (and
// deterministic to test) rather than an MUI Autocomplete.
function Picker({
  label, placeholder, testidPrefix, search, setSearch, results, onPick,
  selected, onClear, renderPrimary, renderSecondary, loading,
}) {
  if (selected) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">{label}:</Typography>
        <Chip
          color="primary"
          label={renderPrimary(selected)}
          data-testid={`${testidPrefix}-selected`}
          onDelete={onClear}
          deleteIcon={<Clear data-testid={`${testidPrefix}-clear`} />}
        />
      </Box>
    );
  }
  return (
    <Box>
      <TextField
        label={label}
        placeholder={placeholder}
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
        inputProps={{ 'data-testid': `${testidPrefix}-search` }}
      />
      {loading && <CircularProgress size={18} sx={{ mt: 1 }} />}
      {!loading && results.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 260, overflow: 'auto' }}>
          <List dense disablePadding>
            {results.map((r) => (
              <ListItemButton
                key={r.id}
                data-testid={`${testidPrefix}-option-${r.id}`}
                onClick={() => onPick(r)}
              >
                <ListItemText
                  primary={renderPrimary(r)}
                  secondary={renderSecondary ? renderSecondary(r) : undefined}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}

export default function CreateWorkOrderV2() {
  usePageTitle('Create Work Order V2');
  const navigate = useNavigate();

  // Product
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [product, setProduct] = useState(null);

  // Order link (optional)
  const [orderSearch, setOrderSearch] = useState('');
  const [orderResults, setOrderResults] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [orderLines, setOrderLines] = useState([]);
  const [lineId, setLineId] = useState('');

  // Details
  const [quantity, setQuantity] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [mode, setMode] = useState('explode'); // 'explode' | 'top_only'
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const debouncedProduct = useDebounced(productSearch);
  const debouncedOrder = useDebounced(orderSearch);

  // Product search — manufacturable items only.
  useEffect(() => {
    if (!debouncedProduct || product) { setProductResults([]); return undefined; }
    let cancelled = false;
    (async () => {
      setProductLoading(true);
      try {
        const res = await manufacturingAPI.items.list({
          search: debouncedProduct, page_size: 25,
        });
        if (cancelled) return;
        const rows = res.data?.results || res.data || [];
        setProductResults(rows.filter(
          (it) => MANUFACTURABLE_TYPES.has(it.item_type) && it.is_active !== false,
        ));
      } catch (err) {
        console.error('Item search failed', err);
      } finally {
        if (!cancelled) setProductLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedProduct, product]);

  // Order search.
  useEffect(() => {
    if (!debouncedOrder || order) { setOrderResults([]); return undefined; }
    let cancelled = false;
    (async () => {
      setOrderLoading(true);
      try {
        const res = await manufacturingAPI.customerOrders.list({
          search: debouncedOrder, page_size: 15,
        });
        if (cancelled) return;
        setOrderResults(res.data?.results || res.data || []);
      } catch (err) {
        console.error('Order search failed', err);
      } finally {
        if (!cancelled) setOrderLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedOrder, order]);

  // Lines of the picked order.
  useEffect(() => {
    if (!order) { setOrderLines([]); setLineId(''); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await manufacturingAPI.customerOrders.get(order.id);
        if (cancelled) return;
        setOrderLines(res.data?.lines || []);
      } catch (err) {
        console.error('Order detail failed', err);
        if (!cancelled) setOrderLines([]);
      }
    })();
    return () => { cancelled = true; };
  }, [order]);

  const lineLabel = (l) => {
    const code = l.product_code || l.product?.item_code || l.product_detail?.item_code || '';
    return `Line ${l.line_number}${code ? ` — ${code}` : ''} (ordered ${l.quantity_ordered})`;
  };

  const canSubmit = !submitting
    && product != null
    && (parseFloat(quantity) || 0) > 0
    && (!order || lineId !== '');

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        output_item: product.id,
        quantity: parseFloat(quantity),
        explode: mode === 'explode',
      };
      if (order && lineId !== '') payload.customer_order_line = lineId;
      if (dueDate) payload.due_date = dueDate;
      if (notes) payload.notes = notes;

      const res = await v2.workOrders.createManual(payload);
      const wo = res.data?.work_order;
      toast.success(
        `Created ${wo?.wo_number || 'work order'}`
        + (mode === 'explode' ? ' (full BOM explosion)' : ' (top level only)'),
      );
      navigate('/manufacturing/v2/work-orders');
    } catch (err) {
      console.error('Manual WO creation failed', err);
      setError(err?.response?.data?.error || 'Failed to create work order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 760 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Factory color="primary" />
        <Typography variant="h4">Create Work Order (V2)</Typography>
      </Box>

      <Paper sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" gutterBottom>What are we making?</Typography>
        <Picker
          label="Product"
          placeholder="Search by name or code…"
          testidPrefix="product"
          search={productSearch}
          setSearch={setProductSearch}
          results={productResults}
          loading={productLoading}
          selected={product}
          onPick={(it) => { setProduct(it); setProductResults([]); }}
          onClear={() => { setProduct(null); setProductSearch(''); }}
          renderPrimary={(it) => `${it.name} (${it.item_code})`}
          renderSecondary={(it) => it.item_type}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Quantity"
            type="number"
            size="small"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputProps={{ 'data-testid': 'manual-qty', min: 0 }}
            sx={{ width: 160 }}
          />
          <TextField
            label="Due date (optional)"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            inputProps={{ 'data-testid': 'manual-due' }}
            sx={{ width: 200 }}
          />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <FormControl>
          <FormLabel sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            How should it be created?
          </FormLabel>
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel
              value="explode"
              control={<Radio inputProps={{ 'data-testid': 'mode-explode' }} />}
              label={(
                <Box>
                  <Typography variant="body2" fontWeight="bold">Full BOM explosion</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Also creates work orders for every sub-assembly and reserves materials —
                    same as sending an order to V2.
                  </Typography>
                </Box>
              )}
            />
            <FormControlLabel
              value="top_only"
              control={<Radio inputProps={{ 'data-testid': 'mode-top-only' }} />}
              label={(
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Top level only (no explosion)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    One &quot;empty&quot; work order for just this item — no sub-assembly work
                    orders, nothing reserved up front. Materials are checked when the
                    step starts. Use this to build the top of a BOM from parts already
                    in stock.
                  </Typography>
                </Box>
              )}
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="subtitle2" gutterBottom>
          Link to a customer order (optional)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Leave empty to build for stock — the work order won&apos;t be allocated to
          any order.
        </Typography>
        <Picker
          label="Customer order"
          placeholder="Search order number or customer…"
          testidPrefix="order"
          search={orderSearch}
          setSearch={setOrderSearch}
          results={orderResults}
          loading={orderLoading}
          selected={order}
          onPick={(o) => { setOrder(o); setOrderResults([]); }}
          onClear={() => { setOrder(null); setOrderSearch(''); setLineId(''); }}
          renderPrimary={(o) => `${o.order_number} — ${o.customer_name || ''}`}
          renderSecondary={(o) => o.status}
        />
        {order && (
          <TextField
            select
            label="Order line"
            size="small"
            fullWidth
            value={lineId}
            onChange={(e) => setLineId(e.target.value)}
            sx={{ mt: 1.5 }}
            SelectProps={{ native: true, inputProps: { 'data-testid': 'manual-line' } }}
            InputLabelProps={{ shrink: true }}
          >
            <option value="" aria-label="No line selected" />
            {orderLines.map((l) => (
              <option key={l.id} value={l.id}>{lineLabel(l)}</option>
            ))}
          </TextField>
        )}

        <Divider sx={{ my: 2.5 }} />

        <TextField
          label="Notes (optional)"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          inputProps={{ 'data-testid': 'manual-notes' }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="manual-error">
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
          <Button onClick={() => navigate('/manufacturing/v2/work-orders')} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <AddCircle />}
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="manual-submit"
          >
            {submitting ? 'Creating…' : 'Create work order'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
